import React, { Fragment, useState } from 'react';

import debounce from 'lodash/debounce';
import { FormattedMessage, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { LinkButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import { ErrorMessage } from '@atlaskit/form';
import ArrowUpRightIcon from '@atlaskit/icon/core/arrow-up-right';
import LinkIcon from '@atlaskit/icon/core/link';
import StatusSuccessIcon from '@atlaskit/icon/core/migration/status-success--check-circle';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import Textfield, { type TextFieldProps } from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';

import { MediaContentHelperMessage } from '../../media-picker/ui/helper-message';
import { isImageURL, isValidUrl, normalizeUrl } from '../../utils';
// TODO: Resolve circular dependency and remove explicit evaluation of showCustomUploadedImage in below component
// import { getImageTypeToShow } from '../../../media-components';
import type { SelectedImage } from '../types';

import { messages } from './messages';

type BrowseButtonAndLinkInputProps = {
	analyticsSource: string;
	currentUrl: string | undefined;
	handleImageSelection: (props: SelectedImage) => void;
	isDisabled?: boolean;
};

const styles = cssMap({
	elemBeforeInput: {
		paddingLeft: token('space.100'),
	},
	elemAfterInput: {
		paddingRight: token('space.100'),
	},
});

export const BrowseButtonAndLinkInput = ({
	currentUrl,
	analyticsSource,
	handleImageSelection,
	isDisabled = false,
}: BrowseButtonAndLinkInputProps) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isFileId = Boolean(currentUrl?.match(/([0-9a-fA-F-]{36})/));
	const showCustomUploadedImage = !!currentUrl && isFileId;

	const [url, setUrl] = useState(showCustomUploadedImage ? '' : currentUrl);
	const [isValidImage, setIsValidImage] = useState(false);
	// TODO: submit to error monitoring service once we find CSM use for this
	const [error, setError] = useState(false);

	const validateUrl = debounce(async (urlToValidate: string) => {
		const trimmedUrl = urlToValidate.trim();

		if (!trimmedUrl) {
			setError(false);
			setIsValidImage(false);
			return;
		}

		if (!isValidUrl(trimmedUrl)) {
			setError(true);
			setIsValidImage(false);
			return;
		}

		const normalizedUrl = normalizeUrl(trimmedUrl);
		const isImage = await isImageURL(normalizedUrl);

		if (!isImage) {
			setError(true);
			setIsValidImage(false);
			return;
		}

		handleImageSelection({
			fullSizeImageUrl: normalizedUrl,
			altDescription: '',
		});

		setError(false);
		setIsValidImage(true);

		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'entered',
				actionSubject: 'url',
				source: analyticsSource,
				attributes: {
					imageSource: 'BrowseButtonAndLinkInput',
				},
			},
		}).fire();
	}, 300);

	const handleUrlInput: TextFieldProps['onChange'] = (e) => {
		const newUrl = e.currentTarget.value;
		setUrl(newUrl);
		validateUrl(newUrl);
	};

	return (
		<Fragment>
			<Textfield
				isDisabled={isDisabled}
				onChange={handleUrlInput}
				placeholder={formatMessage(messages.placeholderText)}
				elemBeforeInput={
					<Box xcss={styles.elemBeforeInput}>
						<LinkIcon color={token('color.icon')} label="" />
					</Box>
				}
				elemAfterInput={
					isValidImage ? (
						<Box xcss={styles.elemAfterInput}>
							<StatusSuccessIcon
								spacing="spacious"
								label="valid"
								color={token('color.icon.success')}
							/>
						</Box>
					) : undefined
				}
				value={url}
			/>
			{error && (
				<ErrorMessage>
					<FormattedMessage {...messages.errorText} />
				</ErrorMessage>
			)}
			<MediaContentHelperMessage
				isDisabled={isDisabled}
				helperMessage={<FormattedMessage {...messages.inspirationText} />}
			/>
			<LinkButton
				appearance="default"
				href="https://unsplash.com"
				isDisabled={isDisabled}
				rel="noopener noreferrer"
				shouldFitContainer
				target="_blank"
			>
				<Flex gap="space.100" alignItems="center" justifyContent="center">
					<FormattedMessage {...messages.buttonText} />
					<ArrowUpRightIcon label="" size="small" />
				</Flex>
			</LinkButton>
		</Fragment>
	);
};
